import { useEffect, useMemo, useRef, FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { useMariaToken } from '../../hooks/useMariaToken';

declare global {
  interface Window {
    MarIA?: {
      init: (config: { apiUrl: string; token?: string; businessId?: string }) => void;
      destroy: () => void;
    };
  }
}

const EXCLUDED_ROUTES = [
  '/signup',
  '/login',
  '/reset-password',
  '/signup-with-invitation',
  '/verifyemailsent',
  '/create-new-password',
  '/email-verification',
  '/track-invoice/',
];

const WIDGET_SRC = process.env.REACT_APP_MARIA_WIDGET_SRC || '';
const API_URL = process.env.REACT_APP_MARIA_API_URL || '';

type InitConfig = {
  apiUrl: string;
  token?: string;
  businessId?: string;
};

const MarIAWidget: FC = () => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);

  const canFetchToken = !!user?.isCompanyUser && !user?.isLender;

  const { data: tokenData } = useMariaToken(
    canFetchToken ? user?.id : undefined,
    selectedCompany?.id,
  );

  const initConfig = useMemo<InitConfig | null>(() => {
    if (!user || !API_URL || !WIDGET_SRC) return null;
    if (tokenData) {
      return { apiUrl: API_URL, token: tokenData.token, businessId: tokenData.businessId };
    }
    return null;
  }, [user, tokenData]);

  const prevInitConfigRef = useRef<InitConfig | null>(null);

  useEffect(() => {
    return () => {
      window.MarIA?.destroy();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      window.MarIA?.destroy();
    }
  }, [user]);

  useEffect(() => {
    if (EXCLUDED_ROUTES.some((r) => location.pathname.startsWith(r))) {
      window.MarIA?.destroy();
      prevInitConfigRef.current = null;
      return;
    }
    if (!initConfig) return;

    if (prevInitConfigRef.current !== null && prevInitConfigRef.current !== initConfig) {
      window.MarIA?.destroy();
    }
    prevInitConfigRef.current = initConfig;

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${WIDGET_SRC}"]`);
    const script = existing || document.createElement('script');

    const onLoad = () => window.MarIA?.init(initConfig);

    if (!existing) {
      script.src = WIDGET_SRC;
      script.async = true;
      script.addEventListener('load', onLoad);
      document.body.appendChild(script);
    } else if (window.MarIA) {
      onLoad();
    } else {
      script.addEventListener('load', onLoad);
    }

    return () => script.removeEventListener('load', onLoad);
  }, [location.pathname, initConfig]);

  return null;
};

export default MarIAWidget;
